import { useProjects } from "@features/projects";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import { Stack, Typography } from "@mui/material";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { RackSegment } from "./RackSegment";

/**
 * "What am I working on right now?" Surfaces the active project's
 * human name. Versions are per-board now, so showing one project-
 * level version chip was structurally misleading on multi-board
 * projects — the board picker / per-board surfaces own version state.
 *
 * Click → project overview (not chat — chat has its own affordance,
 * and this segment is "what am I scoped to", not "open conversation").
 */
export function ProjectSegment() {
  const navigate = useNavigate();
  const activeProjectId = useProjectStore((s) => s.activeProject);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const { data: projects } = useProjects(activeOrgId ?? "personal");

  const project = useMemo(
    () =>
      activeProjectId
        ? projects?.find((p) => p.id === activeProjectId)
        : undefined,
    [activeProjectId, projects],
  );

  if (!activeProjectId) {
    return (
      <RackSegment label="PROJECT" width={260} empty>
        <Typography variant="caption" color="text.secondary">
          No project pinned
        </Typography>
      </RackSegment>
    );
  }

  return (
    <RackSegment
      label="PROJECT"
      width={260}
      ariaLabel={`Open ${project?.name ?? "project"} overview`}
      onClick={() =>
        project?.id &&
        navigate({
          to: "/projects/$projectId/overview",
          params: { projectId: project.id },
        })
      }
    >
      <Stack
        direction="row"
        spacing={0.875}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <DeveloperBoardIcon sx={{ color: "primary.light", fontSize: 14 }} />
        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {project?.name ?? "…"}
        </Typography>
      </Stack>
    </RackSegment>
  );
}
